import type { PageRegion } from './PdfSource'

/** Regions use the displayed (rotated and cropped) PDF page, in units of 0..1.
 * The extractor stores a text line as [x0, y0, x1, y1] (services/workbench extract.pdf_records);
 * operator-drawn regions arrive as {x, y, width, height}. Both are accepted, nothing is guessed:
 * a record without a valid region yields no marker. */
function pageRegion(value: unknown): PageRegion | undefined {
  let x: number, y: number, width: number, height: number
  if (Array.isArray(value) && value.length === 4 && value.every(Number.isFinite)) {
    const [x0, y0, x1, y1] = value as number[]
    ;[x, y, width, height] = [x0, y0, x1 - x0, y1 - y0]
  } else if (value && typeof value === 'object' && ['x', 'y', 'width', 'height'].every(key => Number.isFinite((value as Record<string, unknown>)[key]))) {
    ({ x, y, width, height } = value as PageRegion)
  } else return undefined
  // Rounding at the page edge may leave 1.0000001; anything further out is a broken record.
  const edge = 1e-6
  if (x < 0 || y < 0 || width <= 0 || height <= 0 || x + width > 1 + edge || y + height > 1 + edge) return undefined
  return { x, y, width: Math.min(width, 1 - x), height: Math.min(height, 1 - y) }
}

export function pdfRegions(metadata: Record<string, unknown>, anchor: string, keys: string[]) {
  const regions = new Map<string, PageRegion>()
  for (const record of Array.isArray(metadata.records) ? metadata.records : []) {
    if (!record || typeof record.key !== 'string' || !keys.includes(record.key)) continue
    const region = pageRegion(record.region)
    if (region) regions.set(record.key, region)
  }
  const highlight = regions.get(anchor)
  // Open on the whole table row, not on an isolated cell: the mark and the value must be readable
  // together. Only marks on the anchor's own text line join the focus area.
  let focus = highlight
  if (highlight) for (const [key, region] of regions) {
    const middle = region.y + region.height / 2
    if (key === anchor || middle < highlight.y || middle > highlight.y + highlight.height) continue
    const x = Math.min(focus!.x, region.x), y = Math.min(focus!.y, region.y)
    focus = { x, y, width: Math.max(focus!.x + focus!.width, region.x + region.width) - x,
      height: Math.max(focus!.y + focus!.height, region.y + region.height) - y }
  }
  return {
    highlight,
    focus,
    markers: [...regions].filter(([key]) => key !== anchor).map(([key, region]) => ({ key, ...region })),
    // Requested addresses that exist in the source but carry no usable area: the viewer says so
    // instead of showing a page with no mark and no explanation.
    unlocated: keys.filter(key => !regions.has(key)),
  }
}
